// Contrail V3.9.3 — team chat for your own company (lives in the "Your team" menu).
// Messages: table team_messages (RLS: members of the company). Writes through RPCs team_post / team_delete / team_mark_read
// (migration 0017). Live via Supabase Realtime, with a 60 s poll as a fallback. Unread @mentions drive the red bubble on the
// "online" button; other unread messages show a small dot.
import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "./supabase";

export type TeamMsg = { id: number; org_id: string; user_id: string | null; body: string; mentions: string[]; created_at: string; deleted_at: string | null };
export type Unread = { mentions: number; messages: number };

const PAGE = 100;

export function useTeamChat(orgId: string | null | undefined, open: boolean) {
  const [msgs, setMsgs] = useState<TeamMsg[] | null>(null);
  const [unread, setUnread] = useState<Unread>({ mentions: 0, messages: 0 });
  const [err, setErr] = useState<string | null>(null);
  const openRef = useRef(open); openRef.current = open;
  const loaded = useRef(false);

  const refreshUnread = useCallback(async () => {
    if (!orgId) return;
    const { data } = await supabase.rpc("team_unread", { p_org: orgId });
    if (data) setUnread({ mentions: Number(data.mentions ?? 0), messages: Number(data.messages ?? 0) });
  }, [orgId]);

  const markRead = useCallback(async () => {
    if (!orgId) return;
    setUnread({ mentions: 0, messages: 0 });
    await supabase.rpc("team_mark_read", { p_org: orgId }).then(() => undefined, () => undefined);
  }, [orgId]);

  const load = useCallback(async () => {
    if (!orgId) return;
    const { data, error } = await supabase.from("team_messages").select("*").eq("org_id", orgId)
      .order("created_at", { ascending: false }).limit(PAGE);
    if (error) { setErr(error.message); return; }
    setErr(null); loaded.current = true;
    setMsgs(((data ?? []) as TeamMsg[]).reverse());
  }, [orgId]);

  // unread counter: on start, every 60 s, and when the tab comes back
  useEffect(() => {
    if (!orgId) return;
    refreshUnread();
    const t = setInterval(() => { if (!openRef.current) refreshUnread(); else load(); }, 60_000);
    const vis = () => { if (document.visibilityState === "visible") (openRef.current ? load() : refreshUnread()); };
    document.addEventListener("visibilitychange", vis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", vis); };
  }, [orgId, refreshUnread, load]);

  // live: new messages and deletions
  useEffect(() => {
    if (!orgId) return;
    const ch = supabase.channel(`team_chat_${orgId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "team_messages", filter: `org_id=eq.${orgId}` }, (payload) => {
        const row = payload.new as TeamMsg;
        if (!row?.id) return;
        setMsgs((m) => {
          if (!m) return m;
          const i = m.findIndex((x) => x.id === row.id);
          if (i >= 0) { const n = m.slice(); n[i] = row; return n; }
          return [...m, row].slice(-PAGE * 2);
        });
        if (payload.eventType === "INSERT") {
          if (openRef.current && document.visibilityState === "visible") markRead();
          else refreshUnread();
        }
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [orgId, markRead, refreshUnread]);

  // opening the menu loads the chat and clears the counters
  useEffect(() => {
    if (!open || !orgId) return;
    load(); markRead();
  }, [open, orgId, load, markRead]);

  const send = useCallback(async (body: string, mentions: string[]) => {
    if (!orgId) return;
    const { data, error } = await supabase.rpc("team_post", { p_org: orgId, p_body: body, p_mentions: mentions });
    if (error) throw new Error(error.message);
    const row = data as TeamMsg;
    if (row?.id) setMsgs((m) => (m && !m.some((x) => x.id === row.id) ? [...m, row] : m));
  }, [orgId]);

  const remove = useCallback(async (id: number) => {
    const { error } = await supabase.rpc("team_delete", { p_id: id });
    if (error) throw new Error(error.message);
    setMsgs((m) => m?.map((x) => (x.id === id ? { ...x, body: "", mentions: [], deleted_at: new Date().toISOString() } : x)) ?? m);
  }, []);

  return { msgs, unread, err, send, remove, reload: load };
}

/** "@query" being typed just before the caret, or null. */
export function mentionQuery(text: string, caret: number): { start: number; q: string } | null {
  const before = text.slice(0, caret);
  const m = /(^|\s)@([^\s@]{0,30})$/.exec(before);
  return m ? { start: caret - m[2].length - 1, q: m[2] } : null;
}
